import { EventEmitter, Injectable, Output } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SNItemFactory } from '../models/snitemfactory';
import { CNPatient } from '../models/cnpatient';
import { CNConsultation, CNConsultationType } from '../models/cnconsultation';
import { BecareService } from './becare.service';
import { PatientService } from './patient.service';
import { AppInitService } from './app-init.service';
import { CNContactType } from '../models/cncontacttype';
import { UserConfigService } from './user-config.service';
import { SNUser } from '../models/snuser';
import { CNServiceCode } from '../models/cnservicecode';
import { CNDocumentReport } from '../models/cndocumentreport';
import { SNRestResponse } from '../models/snrestresponse';

@Injectable({
  providedIn: 'root'
})
export class ContactService {

  private static instance: ContactService = null;

  private currentpatient: CNPatient;
  private contacts: CNConsultation[];
  private contacttypes: CNContactType[];

  private user: SNUser;

  public dataStart = 0;
  public dataLimit = 10;
  public dataLength = 0;
  public useOld = false;

  public data = new BehaviorSubject<CNConsultation[]>([]);
  public types = new BehaviorSubject<CNContactType[]>([]);

  @Output() clear = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<CNConsultation>();
  @Output() addCodes = new EventEmitter<CNServiceCode[]>();

  private contactTypeIconMapping: Map<string, string[]> = new Map();

  constructor(private becare: BecareService, private patient: PatientService, private userConfig: UserConfigService) {
    ContactService.instance = this;
    this.patient.patientSelectedEvent.subscribe(patientselected => {
      this.currentpatient = patientselected;
      this.dataStart = 0;
      this.dataLength = 0;
      this.data.next([]);
      this.clear.emit(true);
      this.load();
    });
    this.userConfig.configLoaded.subscribe(() => {
      this.user = this.userConfig.user;
      if (this.userConfig.profileValue('CNConsultation', 'oldwindow')) {
        this.useOld = true;
      }
      this.loadContactTypes();
    });
    this.contactTypeIconMapping.set(CNConsultationType.Simple, ['fas', 'file']);
    this.contactTypeIconMapping.set(CNConsultationType.RX, ['fas', 'radiation']);
    this.contactTypeIconMapping.set(CNConsultationType.Echography, ['fas', 'waveform']);
    this.contactTypeIconMapping.set(CNConsultationType.Kine, ['fas', 'child']);
    this.contactTypeIconMapping.set(CNConsultationType.Logo, ['fas', 'comment']);
    this.contactTypeIconMapping.set(CNConsultationType.Ophtalmo, ['fas', 'glasses']);
    this.contactTypeIconMapping.set(CNConsultationType.Cardio, ['fas', 'heartbeat']);
  }

  public static getInstance(): ContactService {
    return ContactService.instance;
  }

  public load() {
      // Query patient contacts
      this.becare.call('/fr/patient/' + this.currentpatient.id + '/contact?start=' + this.dataStart + '&limit=' + this.dataLimit).
        subscribe(result => {
          if (result.success === true && result.contacts !== undefined) {
            const factory = new SNItemFactory();
            this.contacts = factory.itemsFromJson(result.contacts);
            this.dataLength = result.total;
            this.data.next(this.contacts);
          }
      });
  }

  public loadContactTypes(user?: SNUser) {
    let userToUse = this.user;
    if (user) {
      userToUse = user;
    }
    this.becare.call('/fr/user/' + userToUse.id + '/contacttype').
      subscribe(result => {
        if (result.success === true && result.types !== undefined) {
          const factory = new SNItemFactory();
          this.contacttypes = factory.itemsFromJson(result.types);
          this.types.next(this.contacttypes);
        }
    });
  }

  public typeIcon(type: CNConsultationType): string[] {
    if (this.contactTypeIconMapping.has(type)) {
      return this.contactTypeIconMapping.get(type);
    }
    return ['fas', 'question'];
  }

  save(consultation: CNConsultation, patient?: CNPatient): void {
    let patientid = this.patient.patient.id;
    if (patient) {
      patientid = patient.id;
    }
    this.becare.call('/fr/patient/' + patientid + '/contact', [], consultation.toObj(), 'post').subscribe(result => {
      const response = new SNRestResponse(result);
      if (response.valid && response.success) {
        this.load();
        const factory = new SNItemFactory();
        const savedConsultation = factory.itemFromJson(response.result('consultation'));
        // emit event
        this.saved.emit(savedConsultation);
      }
    });
  }

  applyContactGroup(group: string) {
    let codes: CNServiceCode[] = [];
    this.contacttypes.forEach(type => {
      if (type.name === group) {
        codes = type.codes;
      }
    });
    this.addCodes.emit(codes);
  }

  documents(contact: CNConsultation, patient?: CNPatient): Observable<CNDocumentReport[]> {
    return new Observable(observer => {
      let patientid = this.patient.patient.id;
      if (patient) {
        patientid = patient.id;
      }
      this.becare.call('/fr/patient/' + patientid + '/contact/' + contact.id + '/documents').subscribe(result => {
        const response = new SNRestResponse(result);
        if (!response.valid || !response.success) {
          observer.error(response.message);
        } else {
          const factory = new SNItemFactory();
          observer.next(factory.itemsFromJson(response.result('documents')));
        }
      });
    });
  }
}
